import { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { styles } from "../styles";
import { displayName, hasCapability } from "../utils/capabilities";
import {
  exportPdf,
  loadImageAsDataUrl,
  printPdf,
  shouldShowPdfChooser,
} from "../utils/pdfExport";
import PdfActionModal from "../components/PdfActionModal";
import ProgressBillModal from "../components/ProgressBillModal";
import { buildInvoicePdfDoc } from "../utils/invoicePdf";
import {
  isTaxCertExpired,
  isEffectivelyTaxExempt,
} from "../utils/billTotals";
import {
  VOID_REASON_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
} from "../models/models";
import PaymentEntryModal from "../components/PaymentEntryModal";

const PAYMENT_METHOD_LABEL = Object.fromEntries(
  PAYMENT_METHOD_OPTIONS.map((o) => [o.value, o.label])
);

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Job costing editor (Phase 4.1d). Lives on the job doc under job.costing:
 *   { labor: [...], materials: [...], expenses: [...], fuel: [...],
 *     billDescription, taxes: [...] }
 * (scheduledDate / estimatedPartsArrival now live on the job doc itself,
 * edited from the Job Info card on JobDetailPage.)
 *
 * Labor lines:    hours x rate (rate typed per line — covers skill rates,
 *                 flat hourly, lowered job rates, $0 warranty).
 * Material lines: qty x unitCost, plus a markup % (0 = no markup, can exceed
 *                 100 for rush/specialty items).
 * Expense lines:  flat amount + markup %.
 *
 * Cost = what the work cost you. Billable = cost + markup = the invoice
 * total. Margin = billable - cost.
 */
export default function JobCosting({
  job,
  actions,
  customer,
  location,
  user,
  role,
  capabilities,
  profile = {},
  logoUrl = "",
  orgName = "",
  orgAddress = {},
  quoteDefaults,
}) {
  // Org-level Quote & Bill Defaults seed the cost/rate/markup fields
  // on NEW line items only. Existing lines (edit / save passes)
  // untouched. "" instead of "0" so the user can clear the field
  // cleanly when overriding per-job.
  const dflt = (v) => {
    if (v === null || v === undefined) return "";
    return v === 0 ? "" : String(v);
  };
  const dfltLaborBase = dflt(quoteDefaults?.laborCost);
  const dfltLaborRate = dflt(quoteDefaults?.laborCharge);
  const dfltMaterialMarkup = dflt(quoteDefaults?.materialMarkup);
  const dfltExpenseMarkup = dflt(quoteDefaults?.chargeMarkup);
  const myName = displayName(profile, user?.email);
  const canVoid = hasCapability(role, capabilities, "voidBills");
  const canMarkPaid = hasCapability(role, capabilities, "markPaid");
  // editJobs gates write actions that mutate job state (Reopen Bill,
  // Save Estimated Bill, generate bills). Billing-dept users land here
  // from Closed Bills to re-export PDFs but don't have editJobs and
  // shouldn't see Reopen.
  const canEditJobsInner = hasCapability(role, capabilities, "editJobs");

  // Payment-entry modal target (Phase B AR). When set, modal opens
  // against this bill — caller picks amount + method + date + note;
  // onConfirm appends to bill.payments[] via actions.recordBillPayment.
  const [paymentTarget, setPaymentTarget] = useState(null);

  // Void modal state (Phase A). Holds the bill being voided + reason +
  // optional note. Showing a modal because the reason picker is
  // structured (enum) — a window.confirm wouldn't capture both fields.
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidNote, setVoidNote] = useState("");

  const startVoid = (b) => {
    setVoidTarget(b);
    setVoidReason("");
    setVoidNote("");
  };
  const cancelVoid = () => {
    setVoidTarget(null);
    setVoidReason("");
    setVoidNote("");
  };
  const submitVoid = async () => {
    if (!voidTarget || !voidReason) return;
    if (voidReason === "other" && !voidNote.trim()) return;
    await actions.voidBill(job.id, voidTarget.id, voidReason, voidNote.trim());
    cancelVoid();
  };
  const init = job.costing || {};
  // Backfill defaults so every line has all fields — older saved jobs may
  // predate fields like labor.baseCost, which would otherwise make those
  // inputs flip from uncontrolled to controlled.
  const withDefaults = (list, defaults) =>
    (list || []).map((l) => ({ ...defaults, ...l }));

  // taxable: true default applied to every line. Legacy bills (saved
  // before line-level taxable existed) get true on read, so existing data
  // matches today's behavior (everything taxable). Admin can untoggle a
  // specific line for non-taxable items (most contractors won't touch it).
  //
  // billedOnInvoice: null default — when a Progress Bill includes a line,
  // its billedOnInvoice gets stamped with that bill's INV#. The next
  // Progress Bill editor only shows lines where billedOnInvoice is null.
  const [labor, setLabor] = useState(
    withDefaults(init.labor, {
      description: "",
      hours: "",
      baseCost: "",
      rate: "",
      taxable: true,
      billedOnInvoice: null,
    })
  );
  const [materials, setMaterials] = useState(
    withDefaults(init.materials, {
      name: "",
      partNumber: "",
      qty: "",
      unitCost: "",
      markup: "",
      taxable: true,
      billedOnInvoice: null,
    })
  );
  const [expenses, setExpenses] = useState(
    withDefaults(init.expenses, {
      description: "",
      amount: "",
      markup: "",
      taxable: true,
      billedOnInvoice: null,
    })
  );
  const [fuel, setFuel] = useState(
    withDefaults(init.fuel, {
      mode: "mileage",
      costPerMile: "",
      milesOneWay: "",
      trips: "",
      description: "",
      amount: "",
      markup: "",
      taxable: true,
      billedOnInvoice: null,
    })
  );
  const [billDescription, setBillDescription] = useState(
    init.billDescription || ""
  );
  // Taxes always start with one line present (you can add more) — a bill
  // virtually always has at least a sales tax.
  const taxDefaults = { name: "", mode: "percent", rate: "", amount: "" };
  const initialTaxes = withDefaults(init.taxes, taxDefaults);
  const [taxes, setTaxes] = useState(
    initialTaxes.length
      ? initialTaxes
      : [{ id: newId(), ...taxDefaults }]
  );
  const [billFormat, setBillFormat] = useState("summary");
  const [collapsed, setCollapsed] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  // --- line helpers ---
  const addLabor = () =>
    setLabor((p) => [
      ...p,
      {
        id: newId(),
        description: "",
        hours: "",
        baseCost: dfltLaborBase,
        rate: dfltLaborRate,
        taxable: true,
      },
    ]);
  const addMaterial = () =>
    setMaterials((p) => [
      ...p,
      {
        id: newId(),
        name: "",
        partNumber: "",
        qty: "",
        unitCost: "",
        markup: dfltMaterialMarkup,
        taxable: true,
      },
    ]);
  const addExpense = () =>
    setExpenses((p) => [
      ...p,
      {
        id: newId(),
        description: "",
        amount: "",
        markup: dfltExpenseMarkup,
        taxable: true,
      },
    ]);
  const addFuel = () =>
    setFuel((p) => [
      ...p,
      {
        id: newId(),
        mode: "mileage",
        costPerMile: "",
        milesOneWay: "",
        trips: "",
        description: "",
        amount: "",
        markup: "",
        taxable: true,
      },
    ]);
  const addTax = () =>
    setTaxes((p) => [
      ...p,
      { id: newId(), name: "", mode: "percent", rate: "", amount: "" },
    ]);

  const editLine = (setter) => (id, field, value) =>
    setter((p) => p.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  const removeLine = (setter) => (id) =>
    setter((p) => p.filter((l) => l.id !== id));

  const editLabor = editLine(setLabor);
  const editMaterial = editLine(setMaterials);
  const editExpense = editLine(setExpenses);
  const editFuel = editLine(setFuel);
  const editTax = editLine(setTaxes);

  // --- totals ---
  // Labor cost = hours x base cost (what the tech costs you). Labor billable
  // = hours x bill rate (what the customer is charged). The spread is margin.
  const laborCost = labor.reduce(
    (s, l) => s + num(l.hours) * num(l.baseCost),
    0
  );
  const laborBillable = labor.reduce(
    (s, l) => s + num(l.hours) * num(l.rate),
    0
  );

  const materialCost = materials.reduce(
    (s, m) => s + num(m.qty) * num(m.unitCost),
    0
  );
  const materialBillable = materials.reduce(
    (s, m) => s + num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100),
    0
  );

  const expenseCost = expenses.reduce((s, e) => s + num(e.amount), 0);
  const expenseBillable = expenses.reduce(
    (s, e) => s + num(e.amount) * (1 + num(e.markup) / 100),
    0
  );

  // A fuel/trip line is either mileage-based (cost/mile x one-way miles x 2
  // x trips) or a flat amount.
  const fuelLineCost = (f) =>
    f.mode === "flat"
      ? num(f.amount)
      : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
  const fuelCost = fuel.reduce((s, f) => s + fuelLineCost(f), 0);
  const fuelBillable = fuel.reduce(
    (s, f) => s + fuelLineCost(f) * (1 + num(f.markup) / 100),
    0
  );

  const totalCost = laborCost + materialCost + expenseCost + fuelCost;
  const totalBillable =
    laborBillable + materialBillable + expenseBillable + fuelBillable;
  const margin = totalBillable - totalCost;
  // Margin % is profit / billable (selling) price; markup % is profit / cost.
  const marginPct = totalBillable > 0 ? (margin / totalBillable) * 100 : 0;
  const markupPct = totalCost > 0 ? (margin / totalCost) * 100 : 0;

  // Taxes & fees:
  //   - Percent taxes compute against `taxableSubtotal` — the billable
  //     total of lines whose `taxable` flag is on. A line flagged
  //     non-taxable (rare — specific items the contractor knows are
  //     exempt under their tax authority) drops out of the subtotal but
  //     stays in the bill.
  //   - Flat fees (EPA / environmental / etc.) always apply regardless.
  //   - When the CUSTOMER is taxExempt, all tax lines force to $0.00
  //     (the tax block on the PDF still renders with a cert citation,
  //     never silently disappears).
  const isTaxable = (line) => line?.taxable !== false;
  const laborTaxableBillable = labor.reduce(
    (s, l) => (isTaxable(l) ? s + num(l.hours) * num(l.rate) : s),
    0
  );
  const materialTaxableBillable = materials.reduce(
    (s, m) =>
      isTaxable(m)
        ? s + num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100)
        : s,
    0
  );
  const expenseTaxableBillable = expenses.reduce(
    (s, e) =>
      isTaxable(e) ? s + num(e.amount) * (1 + num(e.markup) / 100) : s,
    0
  );
  const fuelTaxableBillable = fuel.reduce(
    (s, f) =>
      isTaxable(f)
        ? s + fuelLineCost(f) * (1 + num(f.markup) / 100)
        : s,
    0
  );
  const taxableSubtotal =
    laborTaxableBillable +
    materialTaxableBillable +
    expenseTaxableBillable +
    fuelTaxableBillable;

  // Tax-exempt status — snapshot from finalize time wins on already-
  // finalized legacy bills (pre-QB runway #6); drafts / multi-bill /
  // pre-snapshot bills fall back to live EFFECTIVE exempt (taxExempt
  // flag AND cert not expired). Expired cert = exemption invalid =
  // tax owed until renewal.
  const customerExempt =
    job.billFinalized && typeof job.billTaxExemptAtFinalize === "boolean"
      ? job.billTaxExemptAtFinalize
      : isEffectivelyTaxExempt(customer);
  const taxLineAmount = (t) => {
    if (customerExempt) return 0;
    return t.mode === "flat"
      ? num(t.amount)
      : taxableSubtotal * (num(t.rate) / 100);
  };
  const taxAmount = taxes.reduce((s, t) => s + taxLineAmount(t), 0);
  const grandTotal = totalBillable + taxAmount;

  // Quoted-job mode: customer pays the locked quoted price, regardless of
  // what techs add later. Internal P&L still tracks actual costs + the
  // T&M-calculated grandTotal so we can see variance vs the quote.
  const priceMode = init.priceMode || "tm";
  const isQuoted = priceMode === "quoted";
  const quotedAmount = num(init.quotedAmount);
  const customerTotal = isQuoted ? quotedAmount : grandTotal;
  const quoteVariance = isQuoted ? quotedAmount - grandTotal : 0;

  // Ids of cost lines present when this form opened — used to tell apart
  // "the manager deleted this line" (was here at mount) from "a ticket fed a
  // new line after mount" (wasn't here at mount).
  const initialIds = useRef({
    labor: new Set((init.labor || []).map((l) => l.id)),
    materials: new Set((init.materials || []).map((m) => m.id)),
  });

  // Auto-fed ticket lines (sourceTicketId) can arrive AFTER this form opened
  // (a tech files a ticket while the bill is open). Preserve those on save so
  // they aren't clobbered — but don't resurrect lines the manager deleted.
  const mergeFed = (localList, liveList, initialSet) => {
    const localIds = new Set(localList.map((x) => x.id));
    const recovered = (liveList || []).filter(
      (x) =>
        (x.sourceTicketId || x.sourcePOId) &&
        !initialSet.has(x.id) &&
        !localIds.has(x.id)
    );
    return [...localList, ...recovered];
  };

  // Live pickup of ticket-fed lines: when a tech files a ticket while this
  // bill is open, the Cloud Function appends lines to job.costing. Pull in
  // any sourceTicketId line we haven't seen yet — without touching existing
  // local edits or resurrecting a fed line the manager already deleted (once
  // seen, it's never re-added).
  const seenFed = useRef(
    new Set([
      ...(init.labor || []).map((l) => l.id),
      ...(init.materials || []).map((m) => m.id),
    ])
  );
  useEffect(() => {
    const live = job.costing || {};
    const isFed = (x) => x.sourceTicketId || x.sourcePOId;
    const newLabor = (live.labor || []).filter(
      (l) => isFed(l) && !seenFed.current.has(l.id)
    );
    const newMaterials = (live.materials || []).filter(
      (m) => isFed(m) && !seenFed.current.has(m.id)
    );
    if (newLabor.length === 0 && newMaterials.length === 0) return;
    newLabor.forEach((l) => seenFed.current.add(l.id));
    newMaterials.forEach((m) => seenFed.current.add(m.id));
    if (newLabor.length) setLabor((prev) => [...prev, ...newLabor]);
    if (newMaterials.length) setMaterials((prev) => [...prev, ...newMaterials]);
  }, [job.costing]);

  // Sync billedOnInvoice stamps from job.costing into local editor state.
  // When generateBill runs, the server-side transaction stamps the lines
  // it billed. Without this sync, the next Progress Bill modal still sees
  // those lines as unbilled and the editor doesn't visually lock them.
  // Only propagates stamps onto lines that match by id — local edits to
  // unstamped lines are preserved.
  useEffect(() => {
    const live = job.costing || {};
    const stampMap = (list) => {
      const m = new Map();
      for (const row of list || []) {
        if (row?.id && row.billedOnInvoice) m.set(row.id, row.billedOnInvoice);
      }
      return m;
    };
    const apply = (localList, liveMap) => {
      let changed = false;
      const out = localList.map((line) => {
        if (line.billedOnInvoice) return line;
        const stamp = liveMap.get(line.id);
        if (!stamp) return line;
        changed = true;
        return { ...line, billedOnInvoice: stamp };
      });
      return changed ? out : null;
    };
    const lMap = stampMap(live.labor);
    const mMap = stampMap(live.materials);
    const eMap = stampMap(live.expenses);
    const fMap = stampMap(live.fuel);
    const nextLabor = apply(labor, lMap);
    const nextMaterials = apply(materials, mMap);
    const nextExpenses = apply(expenses, eMap);
    const nextFuel = apply(fuel, fMap);
    if (nextLabor) setLabor(nextLabor);
    if (nextMaterials) setMaterials(nextMaterials);
    if (nextExpenses) setExpenses(nextExpenses);
    if (nextFuel) setFuel(nextFuel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.costing]);

  const costingPayload = () => {
    const live = job.costing || {};
    // Spread `live` first so fields the editor doesn't know about
    // (e.g. priceMode, quotedAmount from a quoted job) survive the save.
    // The edited fields below replace what they overlap.
    return {
      ...live,
      labor: mergeFed(labor, live.labor, initialIds.current.labor),
      materials: mergeFed(materials, live.materials, initialIds.current.materials),
      expenses,
      fuel,
      billDescription,
      taxes,
    };
  };

  const save = async () => {
    setSaving(true);
    setSavedMsg("");
    await actions.updateJob(job.id, {
      costing: costingPayload(),
      updatedAt: new Date().toISOString(),
    });
    setSaving(false);
    setSavedMsg("Saved.");
  };

  // Finalize the bill and complete the job — saves the bill, closes the job
  // (moves it to the Completed board), stamps it as finalized, and assigns
  // an INV-YYYY-#### invoice number (per-org counter, transactional). The
  // hand-off to a billing dept / email-bill / online-pay system hooks off
  // this finalized state later.
  const finalizeBill = async () => {
    const ok = window.confirm(
      "Finalize this bill and close the job?\n\nIt moves out of the Completed section into the closed-bill archive. You can still reopen it later if something changes."
    );
    if (!ok) return;
    setSaving(true);
    setSavedMsg("");
    const now = new Date().toISOString();
    await actions.finalizeBillWithInvoice(job.id, {
      costing: costingPayload(),
      status: "closed",
      closedAt: now,
      closedBy: user?.email || "unknown",
      billFinalized: true,
      billFinalizedAt: now,
      billFinalizedBy: user?.email || "unknown",
      billFinalizedByName: myName,
      // Snapshot the customer's EFFECTIVE tax-exempt status + cert AT
      // finalize time so future status flips don't retroactively change
      // tax on this invoice. Pre-QB runway #6 — protects QB sync from
      // "invoice 5012 shows $108.25 today but $100 tomorrow" corruption
      // when a customer gains/loses exemption mid-relationship.
      // "Effective" means taxExempt flag AND cert not expired — an
      // expired cert means the customer was NOT actually exempt at
      // finalize and the snapshot reflects that. Read back via
      // computeLegacyJobTotal in utils/billTotals.js.
      billTaxExemptAtFinalize: isEffectivelyTaxExempt(customer),
      billTaxExemptCertAtFinalize: customer?.taxExemptCert || "",
      updatedAt: now,
    });
    setSaving(false);
    setSavedMsg("Bill finalized — job completed.");
  };

  // Reopen a finalized bill (e.g. correcting an error). Drops the public
  // invoice automatically (mirrorInvoice deletes when billFinalized is false)
  // and re-enables editing. Status stays whatever the user sets next.
  const reopenBill = async () => {
    const ok = window.confirm(
      "Reopen this finalized bill?\n\nIt becomes editable again and the customer-facing invoice will be withdrawn until you finalize again."
    );
    if (!ok) return;
    setSaving(true);
    setSavedMsg("");
    await actions.updateJob(job.id, {
      billFinalized: false,
      billFinalizedAt: null,
      billFinalizedBy: null,
      billFinalizedByName: null,
      updatedAt: new Date().toISOString(),
    });
    setSaving(false);
    setSavedMsg("Bill reopened — edits enabled.");
  };

  const isFinalized = !!job.billFinalized;

  // Generates a real PDF (jsPDF) and exports it via pdfExport helper —
  // downloads on web, opens native Share sheet on mobile. Three formats:
  //   summary  — work description + grand total only
  //   itemized — billed line items, NO contractor cost (the usual bill)
  //   full     — cost + markup + billed per line + margin (gov't jobs that
  //              require complete transparency)
  const exportBill = async (format) => {
    const title =
      format === "full"
        ? "Bill / Invoice — Itemized Cost Breakdown"
        : "Bill / Invoice";

    // Multi-bill jobs: the legacy Export Bill is a CONSOLIDATED view of
    // what's actually been billed across bills[], NOT the full project
    // total. Filter line items to those carrying a billedOnInvoice stamp,
    // and pull subtotal / taxes / grand total from the bills[] array so
    // the math reconciles with what the customer has actually been
    // invoiced for. Per-bill PDFs live in the "Bills issued" panel
    // (exportBillPdf); this consolidated view is the project-recap shape.
    const isMultiBill = (job.bills || []).length > 0;
    const xLabor = isMultiBill
      ? labor.filter((l) => l.billedOnInvoice)
      : labor;
    const xMaterials = isMultiBill
      ? materials.filter((m) => m.billedOnInvoice)
      : materials;
    const xExpenses = isMultiBill
      ? expenses.filter((e) => e.billedOnInvoice)
      : expenses;
    const xFuel = isMultiBill
      ? fuel.filter((f) => f.billedOnInvoice)
      : fuel;
    const xLaborCost = xLabor.reduce(
      (s, l) => s + num(l.hours) * num(l.baseCost),
      0
    );
    const xLaborBillable = xLabor.reduce(
      (s, l) => s + num(l.hours) * num(l.rate),
      0
    );
    const xMaterialCost = xMaterials.reduce(
      (s, m) => s + num(m.qty) * num(m.unitCost),
      0
    );
    const xMaterialBillable = xMaterials.reduce(
      (s, m) => s + num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100),
      0
    );
    const xExpenseCost = xExpenses.reduce(
      (s, e) => s + num(e.amount),
      0
    );
    const xExpenseBillable = xExpenses.reduce(
      (s, e) => s + num(e.amount) * (1 + num(e.markup) / 100),
      0
    );
    const xFuelCost = xFuel.reduce((s, f) => s + fuelLineCost(f), 0);
    const xFuelBillable = xFuel.reduce(
      (s, f) => s + fuelLineCost(f) * (1 + num(f.markup) / 100),
      0
    );
    const xTotalCost =
      xLaborCost + xMaterialCost + xExpenseCost + xFuelCost;
    const xTotalBillable =
      xLaborBillable + xMaterialBillable + xExpenseBillable + xFuelBillable;
    const xMargin = xTotalBillable - xTotalCost;

    // Aggregate taxes across bills, grouped by (name, mode, rate) so a
    // tax that appeared on multiple bills sums cleanly. Falls back to the
    // single-bill totals on legacy jobs.
    const xTaxes = isMultiBill
      ? Object.values(
          (job.bills || []).reduce((acc, b) => {
            for (const t of b.taxes || []) {
              const key = `${t.name || "Tax"}_${t.mode || "percent"}_${t.rate || ""}`;
              if (!acc[key]) {
                acc[key] = { ...t, amount: 0 };
              }
              acc[key].amount += t.amount || 0;
            }
            return acc;
          }, {})
        )
      : taxes;
    const xGrandTotal = isMultiBill
      ? (job.bills || []).reduce((s, b) => s + (b.grandTotal || 0), 0)
      : grandTotal;
    // Multi-bill jobs ALWAYS show billed reality — even quoted jobs that
    // were on the locked-price single-bill path. Once admin uses Progress
    // Bill on a quoted job, the consolidated export should reflect what's
    // been invoiced so far, not the full quoted total (that's the
    // project-value figure, surfaced in the on-screen P&L summary).
    // Single-bill quoted jobs keep today's locked-price behavior.
    const xCustomerTotal = isMultiBill
      ? xGrandTotal
      : isQuoted
      ? quotedAmount
      : grandTotal;
    // Same idea for the "Quoted price (locked)" single-line body row:
    // only render it on single-bill quoted jobs. Multi-bill quoted jobs
    // get the standard Subtotal + tax rows + Total Due structure since
    // each bill is a real partial against the project.
    const showQuotedLockedLine = isQuoted && !isMultiBill;

    const taxRow = (t, descColSpan) => [
      {
        content: `${t.name || "Tax / fee"}${
          t.mode === "percent" && num(t.rate) ? ` (${num(t.rate)}%)` : ""
        }`,
        colSpan: descColSpan,
      },
      { content: money(taxLineAmount(t)), styles: { halign: "right" } },
    ];

    // When the customer is tax-exempt, replace the per-tax-line rows with
    // a single "Tax exempt" row citing the cert number. Keeps the tax
    // block always visible on the printable (per [[project-tax-lines-required]])
    // while clearly communicating the zero amount + reason.
    const exemptRow = (descColSpan) => [
      {
        content: `Sales tax — EXEMPT${
          customer?.taxExemptCert ? ` (Cert ${customer.taxExemptCert})` : ""
        }`,
        colSpan: descColSpan,
        styles: { textColor: 120 },
      },
      {
        content: money(0),
        styles: { halign: "right", textColor: 120 },
      },
    ];
    const taxRowsFor = (descColSpan) =>
      customerExempt
        ? [exemptRow(descColSpan)]
        : taxes.map((t) => taxRow(t, descColSpan));

    const addrLineList = (addr = {}) => {
      const lines = [];
      if (addr.street) lines.push(addr.street);
      const cityLine = [addr.city, addr.state, addr.zip]
        .filter(Boolean)
        .join(", ");
      if (cityLine) lines.push(cityLine);
      if (addr.phone) lines.push(addr.phone);
      return lines;
    };

    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const PAGE_W = doc.internal.pageSize.getWidth();
    const MARGIN = 40;
    const CONTENT_W = PAGE_W - MARGIN * 2;

    // --- Header: logo (if present) + title block ---
    const logoData = await loadImageAsDataUrl(logoUrl);
    let titleX = MARGIN;
    if (logoData) {
      try {
        doc.addImage(logoData, "AUTO", MARGIN, MARGIN, 80, 60);
        titleX = MARGIN + 90;
      } catch (err) {
        console.warn("logo addImage failed:", err);
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(34, 34, 34);
    doc.text(title, titleX, MARGIN + 18);

    let headerY = MARGIN + 18;
    if (orgName) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(102, 102, 102);
      headerY += 16;
      doc.text(orgName, titleX, headerY);
    }
    doc.setFontSize(10);
    doc.setTextColor(85, 85, 85);
    for (const line of addrLineList(orgAddress)) {
      headerY += 12;
      doc.text(line, titleX, headerY);
    }
    headerY = Math.max(headerY, MARGIN + 60);

    // --- Doc meta: invoice # primary, job # + title beneath it.
    // The INV-YYYY-#### invoice number is assigned at finalize time. Pre-
    // finalize exports (admin reviewing a draft bill) lead with the job
    // number; once finalized, the bill leads with the invoice number and
    // the job # + title sit below as the human-readable identifier.
    let y = headerY + 28;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    const dateStr = `Date: ${new Date().toLocaleDateString()}`;
    doc.text(dateStr, PAGE_W - MARGIN - doc.getTextWidth(dateStr), y);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(26, 95, 180);
    if (job.invoiceNumber) {
      doc.text(`Invoice #${job.invoiceNumber}`, MARGIN, y);
      y += 18;
      doc.setFontSize(12);
      doc.setTextColor(34, 34, 34);
      doc.text(
        `Job #${job.jobNumber || "—"}${job.title ? ` — ${job.title}` : ""}`,
        MARGIN,
        y
      );
    } else {
      doc.text(
        `Job #${job.jobNumber || "—"}${job.title ? ` — ${job.title}` : ""}`,
        MARGIN,
        y
      );
    }

    // --- Parties: Bill To (left) + Service Location (right) ---
    y += 22;
    const partyStartY = y;
    const colW = (CONTENT_W - 20) / 2;
    const locColX = MARGIN + colW + 20;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Bill To", MARGIN, y);
    doc.text("Service Location", locColX, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.text(customer?.name || "—", MARGIN, y);
    doc.text(location?.name || "—", locColX, y);
    y += 12;

    const billLines = addrLineList({
      street: customer?.billingStreet,
      city: customer?.billingCity,
      state: customer?.billingState,
      zip: customer?.billingZip,
    });
    const locLines = addrLineList({
      street: location?.street,
      city: location?.city,
      state: location?.state,
      zip: location?.zip,
    });

    let billY = y;
    let locY = y;
    if (billLines.length === 0) {
      doc.setFont("helvetica", "italic");
      doc.setTextColor(136, 136, 136);
      doc.text("No billing address on file", MARGIN, billY);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(34, 34, 34);
      billY += 12;
    } else {
      for (const line of billLines) {
        doc.text(line, MARGIN, billY);
        billY += 12;
      }
    }
    for (const line of locLines) {
      doc.text(line, locColX, locY);
      locY += 12;
    }

    y = Math.max(billY, locY, partyStartY + 38) + 8;

    // --- Description of work ---
    // Job title + jobType are already in the heading ("Job #N — Title")
    // so don't repeat them here. This block is the scope-of-work narrative
    // (billDescription falls back to job.description, then a generic
    // placeholder). Shown on EVERY format so itemized/full PDFs also carry
    // the context — line items alone read as an unannotated price list.
    const descText =
      billDescription || job.description || "Work performed.";
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    doc.text("Description of work", MARGIN, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    const descLinesShared = doc.splitTextToSize(descText, CONTENT_W);
    doc.text(descLinesShared, MARGIN, y);
    y += descLinesShared.length * 13 + 8;

    // --- Body table per format ---
    // bodyStyles adds a faint bottom border to every body row so Subtotal,
    // each tax / fee line, and (in itemized / full) each line item read as
    // distinct rows instead of running together. footStyles' thicker top
    // border separates the Total Due foot from the last body row above.
    const tableBase = {
      startY: y,
      theme: "plain",
      margin: { left: MARGIN, right: MARGIN },
      headStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { bottom: 0.75 },
        lineColor: 200,
      },
      bodyStyles: {
        lineWidth: { bottom: 0.4 },
        lineColor: 215,
      },
      footStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { top: 1.25 },
        lineColor: 80,
      },
    };

    if (format === "summary") {
      // Description of work is already rendered above (shared block). Just
      // start the table at the current y.
      tableBase.startY = y;

      // Quoted jobs: single locked line, customer agreed to a flat rate so
      // the subtotal-then-taxes breakdown is misleading. T&M jobs: the
      // traditional subtotal + taxes structure. For multi-bill jobs, the
      // tax rows come from the aggregated bills[] (xTaxes), not the live
      // costing tax block — same reason as xCustomerTotal: we're showing
      // BILLED reality, not estimated future state.
      const xTaxRows = customerExempt
        ? [exemptRow(1)]
        : isMultiBill
        ? xTaxes.map((t) => [
            { content: `${t.name || "Tax / fee"}${
              t.mode === "percent" && num(t.rate) ? ` (${num(t.rate)}%)` : ""
            }` },
            { content: money(t.amount || 0), styles: { halign: "right" } },
          ])
        : taxRowsFor(1);
      const body = showQuotedLockedLine
        ? [
            [
              { content: "Quoted price (locked)" },
              { content: money(xCustomerTotal), styles: { halign: "right" } },
            ],
          ]
        : [
            [
              { content: "Subtotal" },
              {
                content: money(isMultiBill ? xTotalBillable : totalBillable),
                styles: { halign: "right" },
              },
            ],
            ...xTaxRows,
          ];
      autoTable(doc, {
        ...tableBase,
        body,
        foot: [
          [
            { content: "Total Due", styles: { fontSize: 13 } },
            {
              content: money(xCustomerTotal),
              styles: { halign: "right", fontSize: 13 },
            },
          ],
        ],
        styles: { fontSize: 11, cellPadding: 6 },
        columnStyles: { 1: { halign: "right" } },
      });
    } else if (format === "full") {
      const body = [];
      xLabor.forEach((l) =>
        body.push([
          `Labor${l.description ? " — " + l.description : ""}`,
          `${num(l.hours)} hr`,
          money(num(l.hours) * num(l.baseCost)),
          money(num(l.hours) * num(l.rate)),
        ])
      );
      xMaterials.forEach((m) =>
        body.push([
          `${m.name || "Material"}${
            m.partNumber ? " (#" + m.partNumber + ")" : ""
          }`,
          `${num(m.qty)} @ ${money(num(m.unitCost))} + ${num(m.markup)}%`,
          money(num(m.qty) * num(m.unitCost)),
          money(num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100)),
        ])
      );
      xExpenses.forEach((x) =>
        body.push([
          x.description || "Expense",
          `+ ${num(x.markup)}%`,
          money(num(x.amount)),
          money(num(x.amount) * (1 + num(x.markup) / 100)),
        ])
      );
      xFuel.forEach((f) => {
        const label =
          f.mode === "flat"
            ? f.description || "Trip charge"
            : `Trip charge — ${num(f.trips)} trip(s), ${num(
                f.milesOneWay
              )} mi each way`;
        body.push([
          label,
          `+ ${num(f.markup)}%`,
          money(fuelLineCost(f)),
          money(fuelLineCost(f) * (1 + num(f.markup) / 100)),
        ]);
      });

      const xTaxRowsFull = customerExempt
        ? [exemptRow(3)]
        : isMultiBill
        ? xTaxes.map((t) => [
            {
              content: `${t.name || "Tax / fee"}${
                t.mode === "percent" && num(t.rate) ? ` (${num(t.rate)}%)` : ""
              }`,
              colSpan: 3,
            },
            { content: money(t.amount || 0), styles: { halign: "right" } },
          ])
        : taxRowsFor(3);
      const foot = [
        [
          { content: "Totals", colSpan: 2 },
          { content: money(xTotalCost), styles: { halign: "right" } },
          { content: money(xTotalBillable), styles: { halign: "right" } },
        ],
        [
          { content: "Margin", colSpan: 3 },
          { content: money(xMargin), styles: { halign: "right" } },
        ],
        ...xTaxRowsFull,
        ...(showQuotedLockedLine
          ? [
              [
                {
                  content: `T&M total (cost + markup + tax)`,
                  colSpan: 3,
                  styles: { textColor: 120 },
                },
                {
                  content: money(xGrandTotal),
                  styles: { halign: "right", textColor: 120 },
                },
              ],
              [
                {
                  content: `Quote variance (vs T&M)`,
                  colSpan: 3,
                  styles: { textColor: 120 },
                },
                {
                  content: `${quoteVariance >= 0 ? "+" : ""}${money(quoteVariance)}`,
                  styles: { halign: "right", textColor: 120 },
                },
              ],
            ]
          : []),
        [
          {
            content: showQuotedLockedLine ? "Total Due (quoted, locked)" : "Total Due",
            colSpan: 3,
            styles: { fontSize: 13 },
          },
          {
            content: money(xCustomerTotal),
            styles: { halign: "right", fontSize: 13 },
          },
        ],
      ];

      autoTable(doc, {
        ...tableBase,
        head: [["Description", "Detail", "Cost", "Billed"]],
        body,
        foot,
        styles: { fontSize: 10, cellPadding: 5, overflow: "linebreak" },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { cellWidth: 120 },
          2: { cellWidth: 80, halign: "right" },
          3: { cellWidth: 80, halign: "right" },
        },
      });
    } else {
      // itemized — billed line items, no cost shown
      const body = [];
      xLabor.forEach((l) =>
        body.push([
          `Labor${l.description ? " — " + l.description : ""}`,
          `${num(l.hours)} hr @ ${money(num(l.rate))}`,
          money(num(l.hours) * num(l.rate)),
        ])
      );
      xMaterials.forEach((m) => {
        const amt = num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100);
        const unit = num(m.qty) ? amt / num(m.qty) : 0;
        body.push([
          `${m.name || "Material"}${
            m.partNumber ? " (#" + m.partNumber + ")" : ""
          }`,
          `${num(m.qty)} @ ${money(unit)}`,
          money(amt),
        ]);
      });
      xExpenses.forEach((x) => {
        const amt = num(x.amount) * (1 + num(x.markup) / 100);
        body.push([x.description || "Expense", "", money(amt)]);
      });
      xFuel.forEach((f) => {
        const amt = fuelLineCost(f) * (1 + num(f.markup) / 100);
        const label =
          f.mode === "flat"
            ? f.description || "Trip charge"
            : `Trip charge — ${num(f.trips)} trip(s), ${num(
                f.milesOneWay
              )} mi each way`;
        body.push([label, "", money(amt)]);
      });

      // Quoted jobs: line items above are kept for context but the
      // customer-facing total is the locked quoted price, NOT the sum
      // of the lines (which would be the T&M total). Footer shows the
      // locked total explicitly so the math reads cleanly.
      const xTaxRowsItem = customerExempt
        ? [exemptRow(2)]
        : isMultiBill
        ? xTaxes.map((t) => [
            {
              content: `${t.name || "Tax / fee"}${
                t.mode === "percent" && num(t.rate) ? ` (${num(t.rate)}%)` : ""
              }`,
              colSpan: 2,
            },
            { content: money(t.amount || 0), styles: { halign: "right" } },
          ])
        : taxRowsFor(2);
      const foot = showQuotedLockedLine
        ? [
            [
              {
                content: "Quoted price (locked) — line items above are reference only",
                colSpan: 2,
                styles: { textColor: 120 },
              },
              { content: "", styles: {} },
            ],
            [
              { content: "Total Due", colSpan: 2, styles: { fontSize: 13 } },
              {
                content: money(xCustomerTotal),
                styles: { halign: "right", fontSize: 13 },
              },
            ],
          ]
        : [
            [
              { content: "Subtotal", colSpan: 2 },
              {
                content: money(isMultiBill ? xTotalBillable : totalBillable),
                styles: { halign: "right" },
              },
            ],
            ...xTaxRowsItem,
            [
              { content: "Total Due", colSpan: 2, styles: { fontSize: 13 } },
              {
                content: money(xCustomerTotal),
                styles: { halign: "right", fontSize: 13 },
              },
            ],
          ];

      autoTable(doc, {
        ...tableBase,
        head: [["Description", "Detail", "Amount"]],
        body,
        foot,
        styles: { fontSize: 11, cellPadding: 5, overflow: "linebreak" },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { cellWidth: 160 },
          2: { cellWidth: 90, halign: "right" },
        },
      });
    }

    // Multi-bill consolidated PDFs: list every prior invoice with its INV# +
    // description + amount so the customer can cross-reference against the
    // bills they already received. Matches what QuickBooks Progress Invoicing
    // calls a "Statement of Account" — running list of invoices, not the
    // current period's invoice.
    if (isMultiBill && (job.bills || []).length > 0) {
      const startY = doc.lastAutoTable.finalY + 18;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(34, 34, 34);
      doc.text("Bills included on this statement", MARGIN, startY);

      const billedToDate = (job.bills || []).reduce(
        (s, b) => s + (b.grandTotal || 0),
        0
      );
      autoTable(doc, {
        startY: startY + 6,
        theme: "plain",
        margin: { left: MARGIN, right: MARGIN },
        head: [
          [
            "Invoice #",
            "Description",
            { content: "Date", styles: { halign: "right" } },
            { content: "Amount", styles: { halign: "right" } },
          ],
        ],
        body: (job.bills || []).map((b) => [
          b.invoiceNumber || "—",
          (b.description || "") +
            (b.type === "final" ? " (final)" : ""),
          {
            content: b.finalizedAt
              ? new Date(b.finalizedAt).toLocaleDateString()
              : "",
            styles: { halign: "right" },
          },
          {
            content: money(b.grandTotal || 0),
            styles: { halign: "right" },
          },
        ]),
        foot: [
          [
            {
              content: "Total billed to date",
              colSpan: 3,
              styles: { fontStyle: "bold" },
            },
            {
              content: money(billedToDate),
              styles: { halign: "right", fontStyle: "bold" },
            },
          ],
        ],
        styles: { fontSize: 10, cellPadding: 5 },
        headStyles: {
          fontStyle: "bold",
          fillColor: false,
          textColor: 34,
          lineWidth: { bottom: 0.75 },
          lineColor: 200,
        },
        bodyStyles: {
          lineWidth: { bottom: 0.4 },
          lineColor: 215,
        },
        footStyles: {
          fontStyle: "bold",
          fillColor: false,
          textColor: 34,
          lineWidth: { top: 1.25 },
          lineColor: 80,
        },
        columnStyles: {
          0: { cellWidth: 110 },
          1: { cellWidth: "auto" },
          2: { cellWidth: 80, halign: "right" },
          3: { cellWidth: 90, halign: "right" },
        },
      });
    }

    const dateSlug = new Date().toISOString().slice(0, 10);
    const jobSlug = job.jobNumber || job.id.slice(0, 6);
    const filename = job.invoiceNumber
      ? `Invoice_${job.invoiceNumber}_${dateSlug}.pdf`
      : `Bill_Job-${jobSlug}_${dateSlug}.pdf`;

    // On native (phone), Share sheet IS the chooser — print, save, email
    // are all destinations there. On web we pop our own modal so the user
    // doesn't have to know the browser's print-dialog has Save as PDF in
    // its destination menu.
    if (shouldShowPdfChooser()) {
      setPdfPending({ doc, filename });
    } else {
      await exportPdf(doc, filename);
    }
  };

  // Progress Bill modal open state. null = closed; "partial" / "final" =
  // open in that mode. The modal handles its own selection state internally
  // and calls back with the computed bill payload.
  const [progressBillType, setProgressBillType] = useState(null);
  // Phase D: billing mode for the Progress Bill being generated.
  // Defaults to "line-item" (the existing pre-Phase-D behavior); set to
  // "percentage" by the dedicated button on quoted jobs. Both stored on
  // the bill so JobCosting + InvoicesPage + PDFs can render the right
  // shape per bill.
  const [progressBillMode, setProgressBillMode] = useState("line-item");

  // Locked billing mode for the WHOLE JOB. First non-voided bill decides;
  // subsequent bills must match. Null when nothing's been billed yet so
  // both "Progress (line items)" + "Progress (percentage)" buttons show.
  const lockedBillingMode =
    (job.bills || []).find((b) => !b.voided)?.billingMode || null;
  const canBillPercentage = isQuoted && quotedAmount > 0;
  const showLineItemButtons =
    lockedBillingMode === "line-item" || lockedBillingMode === null;
  const showPercentageButtons =
    canBillPercentage &&
    (lockedBillingMode === "percentage" || lockedBillingMode === null);

  const startBill = (type, mode) => {
    setProgressBillMode(mode);
    setProgressBillType(type);
  };
  const onConfirmBill = async (billData) => {
    // Save any in-flight costing edits first (taxable toggles, freshly
    // added rows) so the generateBill action sees the same state the
    // user just selected. Otherwise the modal's UNBILLED filter could
    // include rows that haven't been persisted yet — the line stamping
    // in generateBill would silently drop them.
    await actions.updateJob(job.id, {
      costing: costingPayload(),
      updatedAt: new Date().toISOString(),
    });
    await actions.generateBill(job.id, billData);

    // Immediately stamp local state for the lines we just billed so the
    // editor locks them right away (don't wait for the realtime listener
    // round-trip). The real INV# from the server will overwrite this
    // placeholder via the useEffect sync above when the listener fires.
    const billedSet = new Set(billData.lineItemIds || []);
    const stampLocal = (list) =>
      list.map((line) =>
        billedSet.has(line.id) && !line.billedOnInvoice
          ? { ...line, billedOnInvoice: "pending" }
          : line
      );
    setLabor((p) => stampLocal(p));
    setMaterials((p) => stampLocal(p));
    setExpenses((p) => stampLocal(p));
    setFuel((p) => stampLocal(p));

    setProgressBillType(null);
    setSavedMsg(
      billData.type === "final"
        ? "Final bill generated — job closed."
        : "Progress bill generated."
    );
  };

  // Export a finalized bill (entry in job.bills[]) as a Summary-format PDF
  // using the same shared invoicePdf helper the facility's print uses.
  const exportBillPdf = async (bill) => {
    const customerBilling = {
      street: customer?.billingStreet,
      city: customer?.billingCity,
      state: customer?.billingState,
      zip: customer?.billingZip,
    };
    const locationAddress = {
      street: location?.street,
      suite: location?.suite || location?.building,
      city: location?.city,
      state: location?.state,
      zip: location?.zip,
    };
    // Running-total context for the customer: sum the grandTotals of
    // bills issued BEFORE this one on the same job. Bills are appended
    // in chronological order, so the slice up to this bill's index works.
    // (Same-day or out-of-order edits could theoretically scramble this
    // — we re-check by finalizedAt for safety.)
    const billsList = job.bills || [];
    const thisIdx = billsList.findIndex((b) => b.id === bill.id);
    const priorBillsSlice =
      thisIdx >= 0 ? billsList.slice(0, thisIdx) : [];
    // Trim each prior bill to just what the helper needs (also keeps the
    // PDF render side-effect-free if the bills[] schema grows fields the
    // PDF should ignore).
    const priorBills = priorBillsSlice.map((b) => ({
      invoiceNumber: b.invoiceNumber || "",
      description: b.description || "",
      grandTotal: b.grandTotal || 0,
      finalizedAt: b.finalizedAt || "",
      type: b.type || "partial",
    }));

    const doc = await buildInvoicePdfDoc({
      orgName,
      orgAddress,
      logoUrl,
      invoiceNumber: bill.invoiceNumber || "",
      jobNumber: job.jobNumber || "",
      jobTitle: job.title || "",
      jobType: job.jobType || "",
      date: bill.finalizedAt ? new Date(bill.finalizedAt) : new Date(),
      customerName: customer?.name || "—",
      customerBilling,
      locationName: location?.name || "—",
      locationAddress,
      workDescription: bill.description || "",
      subtotal: bill.subtotal || 0,
      taxes: bill.taxes || [],
      grandTotal: bill.grandTotal || 0,
      taxExempt: !!customer?.taxExempt,
      taxExemptCert: customer?.taxExemptCert || "",
      priorBills,
      finalizedAt: bill.finalizedAt || "",
      voided: !!bill.voided,
      voidedAt: bill.voidedAt || "",
      voidReason: bill.voidReason || "",
    });
    const dateSlug = new Date().toISOString().slice(0, 10);
    const filename = `Invoice_${bill.invoiceNumber || job.jobNumber}_${dateSlug}.pdf`;
    if (shouldShowPdfChooser()) {
      setPdfPending({ doc, filename });
    } else {
      await exportPdf(doc, filename);
    }
  };

  const [pdfPending, setPdfPending] = useState(null);
  const handlePdfPrint = () => {
    if (!pdfPending) return;
    printPdf(pdfPending.doc);
    setPdfPending(null);
  };
  const handlePdfSave = async () => {
    if (!pdfPending) return;
    await exportPdf(pdfPending.doc, pdfPending.filename);
    setPdfPending(null);
  };

  const cell = { fontSize: "12px", padding: "2px 4px" };
  const smallInput = { ...styles.input, width: "80px", margin: "2px" };

  return (
    <div style={styles.card}>
      <strong
        style={{ cursor: "pointer" }}
        onClick={() => setCollapsed((c) => !c)}
      >
        {collapsed ? "▸" : "▾"} Estimated Bill
      </strong>

      {collapsed ? null : (
      <>
      {/* Past bills on this job (progress bill flow). Empty on legacy
          single-finalize jobs. Each entry surfaces its INV#, type,
          finalized date, total, and an Export PDF button so admin can
          re-download / re-send any prior partial. */}
      {(job.bills || []).length > 0 && (
        <div
          style={{
            marginTop: "10px",
            border: "1px solid #d1e2f8",
            background: "#f3f8ff",
            borderRadius: "4px",
            padding: "10px 12px",
          }}
        >
          <strong style={{ fontSize: "13px", color: "#1a5fb4" }}>
            Bills issued ({job.bills.length})
          </strong>
          {job.bills.map((b) => (
            <div
              key={b.id}
              style={{
                marginTop: "8px",
                padding: "8px",
                background: b.voided ? "#fafafa" : "white",
                border: `1px solid ${b.voided ? "#fca5a5" : "#e0e9f7"}`,
                borderRadius: "3px",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
                opacity: b.voided ? 0.78 : 1,
              }}
            >
              <div style={{ flex: 1, minWidth: "240px" }}>
                <div style={{ fontWeight: 600 }}>
                  <span
                    style={{
                      textDecoration: b.voided ? "line-through" : "none",
                      color: b.voided ? "#999" : "inherit",
                    }}
                  >
                    {b.invoiceNumber || "(no invoice #)"}
                  </span>
                  <span
                    style={{
                      marginLeft: "6px",
                      fontSize: "10px",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      textTransform: "uppercase",
                      background: b.type === "final" ? "#c9efc9" : "#fff3cd",
                      color: b.type === "final" ? "#1a5f1f" : "#7a5400",
                    }}
                  >
                    {b.type}
                  </span>
                  {b.billingMode === "percentage" && (
                    <span
                      style={{
                        marginLeft: "6px",
                        fontSize: "10px",
                        padding: "1px 6px",
                        borderRadius: "3px",
                        textTransform: "uppercase",
                        background: "#e0eaff",
                        color: "#1a5fb4",
                        border: "1px solid #bcd0ee",
                        fontWeight: 600,
                      }}
                      title={`${num(b.percent)}% of ${money(
                        num(b.percentBase)
                      )} contracted price`}
                    >
                      {num(b.percent)}%
                    </span>
                  )}
                  {b.voided && (
                    <span
                      style={{
                        marginLeft: "6px",
                        fontSize: "10px",
                        padding: "1px 6px",
                        borderRadius: "3px",
                        textTransform: "uppercase",
                        background: "#fee2e2",
                        color: "#b91c1c",
                        border: "1px solid #fca5a5",
                        fontWeight: 700,
                      }}
                    >
                      VOIDED
                    </span>
                  )}
                </div>
                <div style={{ color: "#444", marginTop: "2px" }}>
                  {b.description || "(no description)"}
                </div>
                <div style={{ color: "#888", fontSize: "11px", marginTop: "2px" }}>
                  {b.finalizedAt
                    ? new Date(b.finalizedAt).toLocaleDateString()
                    : ""}
                  {b.finalizedByName ? ` · ${b.finalizedByName}` : ""}
                </div>
                {b.voided && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#b91c1c",
                      marginTop: "2px",
                    }}
                  >
                    Voided{" "}
                    {b.voidedAt
                      ? new Date(b.voidedAt).toLocaleDateString()
                      : ""}
                    {b.voidedByName ? ` by ${b.voidedByName}` : ""}
                    {b.voidReason
                      ? ` — ${
                          VOID_REASON_OPTIONS.find(
                            (o) => o.value === b.voidReason
                          )?.label || b.voidReason
                        }`
                      : ""}
                    {b.voidNote ? ` (${b.voidNote})` : ""}
                  </div>
                )}
                {/* Phase B: payment history list when entries exist. */}
                {(b.payments || []).length > 0 && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#1a5f1f",
                      marginTop: "4px",
                      paddingTop: "4px",
                      borderTop: "1px dashed #d0e0d0",
                    }}
                  >
                    <strong>Payments:</strong>
                    {(b.payments || []).map((p) => (
                      <div key={p.id} style={{ marginLeft: "8px" }}>
                        {p.date} ·{" "}
                        {PAYMENT_METHOD_LABEL[p.method] || p.method} ·{" "}
                        {money(p.amount)}
                        {p.note ? ` — ${p.note}` : ""}
                        {p.paidByName ? ` (recorded by ${p.paidByName})` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontWeight: 600,
                  color: b.voided ? "#999" : "#1a5fb4",
                  textDecoration: b.voided ? "line-through" : "none",
                }}
              >
                {money(b.grandTotal || 0)}
              </div>
              <button
                style={{ ...styles.button, padding: "4px 8px" }}
                onClick={() => exportBillPdf(b)}
                type="button"
              >
                Export PDF
              </button>
              {canVoid && !b.voided && b.paymentStatus !== "paid" && (
                <button
                  style={{
                    ...styles.button,
                    padding: "4px 8px",
                    color: "#b91c1c",
                    borderColor: "#fca5a5",
                  }}
                  onClick={() => startVoid(b)}
                  type="button"
                  title="Void this invoice — keeps the INV# on file but marks it voided. Costing lines are freed for re-billing."
                >
                  Void
                </button>
              )}
              {/* Phase B: payment status badge + Record payment button.
                  Voided bills + already-fully-paid bills suppress both. */}
              {!b.voided && (() => {
                const total = num(b.grandTotal);
                const paid = num(b.paidAmount);
                const status =
                  b.paymentStatus ||
                  (paid + 0.005 >= total
                    ? "paid"
                    : paid > 0
                    ? "partial"
                    : "unpaid");
                const palette = {
                  paid: { bg: "#d6f5dd", color: "#1a7f37", border: "#a7e3b3" },
                  partial: {
                    bg: "#fff3cd",
                    color: "#92400e",
                    border: "#facc15",
                  },
                  unpaid: {
                    bg: "#f3f4f6",
                    color: "#6b7280",
                    border: "#e5e7eb",
                  },
                }[status];
                return (
                  <>
                    <span
                      style={{
                        fontSize: "11px",
                        padding: "2px 8px",
                        background: palette.bg,
                        color: palette.color,
                        border: `1px solid ${palette.border}`,
                        borderRadius: "4px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                      }}
                      title={
                        status === "partial"
                          ? `Paid ${money(paid)} of ${money(total)}`
                          : status === "paid"
                          ? `Paid in full${
                              b.lastPaymentAt
                                ? ` on ${new Date(
                                    b.lastPaymentAt
                                  ).toLocaleDateString()}`
                                : ""
                            }`
                          : "Awaiting payment"
                      }
                    >
                      {status === "paid"
                        ? "Paid"
                        : status === "partial"
                        ? `Partial ${money(paid)}`
                        : "Unpaid"}
                    </span>
                    {canMarkPaid && status !== "paid" && (
                      <button
                        style={{
                          ...styles.button,
                          padding: "4px 8px",
                          background: "#1a7f37",
                          color: "white",
                        }}
                        onClick={() => setPaymentTarget(b)}
                        type="button"
                      >
                        Record payment
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          ))}
          <div
            style={{
              marginTop: "8px",
              fontSize: "12px",
              color: "#1a5fb4",
              fontWeight: 600,
            }}
          >
            Total billed:{" "}
            {money(
              (job.bills || []).reduce(
                (s, b) => s + (b.grandTotal || 0),
                0
              )
            )}
          </div>
        </div>
      )}
      <fieldset
        disabled={isFinalized}
        style={{ border: "none", padding: 0, margin: 0 }}
      >
      {/* Bill description (used on the summary bill) */}
      <div style={{ marginTop: "12px" }}>
        <label
          style={{
            fontSize: "13px",
            display: "block",
            marginBottom: "4px",
          }}
        >
          Bill description (scope of work):
        </label>
        <textarea
          style={styles.textarea}
          placeholder="Plain-language summary of the work, shown on the summary bill"
          value={billDescription}
          onChange={(e) => setBillDescription(e.target.value)}
        />
      </div>

      {/* Labor */}
      <div style={{ marginTop: "12px" }}>
        <strong style={{ fontSize: "13px" }}>Labor</strong>
        {labor.map((l) => {
          const billed = !!l.billedOnInvoice;
          const rowStyle = billed
            ? { ...cell, background: "#f0f0f0", opacity: 0.8, borderRadius: "3px" }
            : cell;
          return (
            <div key={l.id} style={rowStyle}>
              <input
                style={{ ...smallInput, width: "140px" }}
                placeholder="Tech / description"
                value={l.description}
                onChange={(e) => editLabor(l.id, "description", e.target.value)}
                disabled={billed}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Hours"
                value={l.hours}
                onChange={(e) => editLabor(l.id, "hours", e.target.value)}
                disabled={billed}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Cost $/hr"
                value={l.baseCost}
                onChange={(e) => editLabor(l.id, "baseCost", e.target.value)}
                disabled={billed}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Bill $/hr"
                value={l.rate}
                onChange={(e) => editLabor(l.id, "rate", e.target.value)}
                disabled={billed}
              />
              <span style={{ fontSize: "12px" }}>
                cost {money(num(l.hours) * num(l.baseCost))} / bill{" "}
                {money(num(l.hours) * num(l.rate))}
              </span>{" "}
              {billed ? (
                <span
                  style={{
                    fontSize: "10px",
                    padding: "1px 6px",
                    background: "#d4edda",
                    color: "#1a5f1f",
                    borderRadius: "3px",
                    fontWeight: 600,
                  }}
                >
                  Billed · {l.billedOnInvoice}
                </span>
              ) : (
                <>
                  <label style={{ fontSize: "11px", marginLeft: "4px" }}>
                    <input
                      type="checkbox"
                      checked={l.taxable !== false}
                      onChange={(e) =>
                        editLabor(l.id, "taxable", e.target.checked)
                      }
                    />{" "}
                    Taxable
                  </label>{" "}
                  <button
                    style={{ ...styles.button, color: "red", padding: "2px 6px" }}
                    onClick={() => removeLine(setLabor)(l.id)}
                  >
                    x
                  </button>
                </>
              )}
            </div>
          );
        })}
        <button style={styles.button} onClick={addLabor}>
          + Labor line
        </button>
      </div>

      {/* Materials */}
      <div style={{ marginTop: "12px" }}>
        <strong style={{ fontSize: "13px" }}>Materials</strong>
        {materials.map((m) => {
          const billed = !!m.billedOnInvoice;
          const rowStyle = billed
            ? { ...cell, background: "#f0f0f0", opacity: 0.8, borderRadius: "3px" }
            : cell;
          return (
            <div key={m.id} style={rowStyle}>
              <input
                style={{ ...smallInput, width: "120px" }}
                placeholder="Part name"
                value={m.name}
                onChange={(e) => editMaterial(m.id, "name", e.target.value)}
                disabled={billed}
              />
              <input
                style={{ ...smallInput, width: "90px" }}
                placeholder="Part #"
                value={m.partNumber}
                onChange={(e) => editMaterial(m.id, "partNumber", e.target.value)}
                disabled={billed}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Qty"
                value={m.qty}
                onChange={(e) => editMaterial(m.id, "qty", e.target.value)}
                disabled={billed}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Unit $"
                value={m.unitCost}
                onChange={(e) => editMaterial(m.id, "unitCost", e.target.value)}
                disabled={billed}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Markup %"
                value={m.markup}
                onChange={(e) => editMaterial(m.id, "markup", e.target.value)}
                disabled={billed}
              />
              <span style={{ fontSize: "12px" }}>
                = {money(num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100))}
              </span>{" "}
              {billed ? (
                <span
                  style={{
                    fontSize: "10px",
                    padding: "1px 6px",
                    background: "#d4edda",
                    color: "#1a5f1f",
                    borderRadius: "3px",
                    fontWeight: 600,
                  }}
                >
                  Billed · {m.billedOnInvoice}
                </span>
              ) : (
                <>
                  <label style={{ fontSize: "11px", marginLeft: "4px" }}>
                    <input
                      type="checkbox"
                      checked={m.taxable !== false}
                      onChange={(e) =>
                        editMaterial(m.id, "taxable", e.target.checked)
                      }
                    />{" "}
                    Taxable
                  </label>{" "}
                  <button
                    style={{ ...styles.button, color: "red", padding: "2px 6px" }}
                    onClick={() => removeLine(setMaterials)(m.id)}
                  >
                    x
                  </button>
                </>
              )}
            </div>
          );
        })}
        <button style={styles.button} onClick={addMaterial}>
          + Material line
        </button>
      </div>

      {/* Other expenses */}
      <div style={{ marginTop: "12px" }}>
        <strong style={{ fontSize: "13px" }}>Other Expenses</strong>
        {expenses.map((x) => {
          const billed = !!x.billedOnInvoice;
          const rowStyle = billed
            ? { ...cell, background: "#f0f0f0", opacity: 0.8, borderRadius: "3px" }
            : cell;
          return (
            <div key={x.id} style={rowStyle}>
              <input
                style={{ ...smallInput, width: "160px" }}
                placeholder="Description (rental, permit, sub...)"
                value={x.description}
                onChange={(e) => editExpense(x.id, "description", e.target.value)}
                disabled={billed}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Amount $"
                value={x.amount}
                onChange={(e) => editExpense(x.id, "amount", e.target.value)}
                disabled={billed}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Markup %"
                value={x.markup}
                onChange={(e) => editExpense(x.id, "markup", e.target.value)}
                disabled={billed}
              />
              <span style={{ fontSize: "12px" }}>
                = {money(num(x.amount) * (1 + num(x.markup) / 100))}
              </span>{" "}
              {billed ? (
                <span
                  style={{
                    fontSize: "10px",
                    padding: "1px 6px",
                    background: "#d4edda",
                    color: "#1a5f1f",
                    borderRadius: "3px",
                    fontWeight: 600,
                  }}
                >
                  Billed · {x.billedOnInvoice}
                </span>
              ) : (
                <>
                  <label style={{ fontSize: "11px", marginLeft: "4px" }}>
                    <input
                      type="checkbox"
                      checked={x.taxable !== false}
                      onChange={(e) =>
                        editExpense(x.id, "taxable", e.target.checked)
                      }
                    />{" "}
                    Taxable
                  </label>{" "}
                  <button
                    style={{ ...styles.button, color: "red", padding: "2px 6px" }}
                    onClick={() => removeLine(setExpenses)(x.id)}
                  >
                    x
                  </button>
                </>
              )}
            </div>
          );
        })}
        <button style={styles.button} onClick={addExpense}>
          + Expense line
        </button>
      </div>

      {/* Fuel / trip charges */}
      <div style={{ marginTop: "12px" }}>
        <strong style={{ fontSize: "13px" }}>Fuel / Trip Charges</strong>
        {fuel.map((f) => {
          const billed = !!f.billedOnInvoice;
          const rowStyle = billed
            ? {
                ...cell,
                marginTop: "4px",
                background: "#f0f0f0",
                opacity: 0.8,
                borderRadius: "3px",
              }
            : { ...cell, marginTop: "4px" };
          return (
            <div key={f.id} style={rowStyle}>
              <select
                style={{ ...smallInput, width: "110px" }}
                value={f.mode}
                onChange={(e) => editFuel(f.id, "mode", e.target.value)}
                disabled={billed}
              >
                <option value="mileage">Per mile</option>
                <option value="flat">Flat amount</option>
              </select>

              {f.mode === "mileage" ? (
                <>
                  <input
                    style={smallInput}
                    type="number"
                    placeholder="$/mile"
                    value={f.costPerMile}
                    onChange={(e) =>
                      editFuel(f.id, "costPerMile", e.target.value)
                    }
                    disabled={billed}
                  />
                  <input
                    style={smallInput}
                    type="number"
                    placeholder="Miles 1-way"
                    value={f.milesOneWay}
                    onChange={(e) =>
                      editFuel(f.id, "milesOneWay", e.target.value)
                    }
                    disabled={billed}
                  />
                  <input
                    style={smallInput}
                    type="number"
                    placeholder="# trips"
                    value={f.trips}
                    onChange={(e) => editFuel(f.id, "trips", e.target.value)}
                    disabled={billed}
                  />
                </>
              ) : (
                <>
                  <input
                    style={{ ...smallInput, width: "160px" }}
                    placeholder="Description"
                    value={f.description}
                    onChange={(e) =>
                      editFuel(f.id, "description", e.target.value)
                    }
                    disabled={billed}
                  />
                  <input
                    style={smallInput}
                    type="number"
                    placeholder="Amount $"
                    value={f.amount}
                    onChange={(e) => editFuel(f.id, "amount", e.target.value)}
                    disabled={billed}
                  />
                </>
              )}

              <input
                style={smallInput}
                type="number"
                placeholder="Markup %"
                value={f.markup}
                onChange={(e) => editFuel(f.id, "markup", e.target.value)}
                disabled={billed}
              />
              <span style={{ fontSize: "12px" }}>
                = {money(fuelLineCost(f) * (1 + num(f.markup) / 100))}
              </span>{" "}
              {billed ? (
                <span
                  style={{
                    fontSize: "10px",
                    padding: "1px 6px",
                    background: "#d4edda",
                    color: "#1a5f1f",
                    borderRadius: "3px",
                    fontWeight: 600,
                  }}
                >
                  Billed · {f.billedOnInvoice}
                </span>
              ) : (
                <>
                  <label style={{ fontSize: "11px", marginLeft: "4px" }}>
                    <input
                      type="checkbox"
                      checked={f.taxable !== false}
                      onChange={(e) =>
                        editFuel(f.id, "taxable", e.target.checked)
                      }
                    />{" "}
                    Taxable
                  </label>{" "}
                  <button
                    style={{ ...styles.button, color: "red", padding: "2px 6px" }}
                    onClick={() => removeLine(setFuel)(f.id)}
                  >
                    x
                  </button>
                </>
              )}
            </div>
          );
        })}
        <button style={styles.button} onClick={addFuel}>
          + Fuel / trip line
        </button>
      </div>

      {/* Customer-level tax-exempt banner. Shown whenever the customer
          doc carries taxExempt: true. Two states:
          - Cert current → no tax charged (customerExempt = true downstream)
          - Cert expired → exemption invalid, tax IS being charged
            (customerExempt = false; contractor must refresh cert) */}
      {customer?.taxExempt && (() => {
        const certExpired = isTaxCertExpired(customer);
        return (
          <div
            style={{
              marginTop: "12px",
              padding: "8px 10px",
              background: certExpired ? "#fdecec" : "#fff8e0",
              border: certExpired ? "1px solid #e0a0a0" : "1px solid #f0d27a",
              borderRadius: "4px",
              fontSize: "12px",
              color: certExpired ? "#8a1f1f" : "#6a4900",
            }}
          >
            {certExpired ? (
              <>
                <strong>TAX EXEMPT customer — cert expired.</strong> Exemption
                is invalid until the cert is refreshed, so sales tax IS being
                applied on this bill.
                {customer?.taxExemptCert
                  ? ` Cert on file: ${customer.taxExemptCert}.`
                  : " (Cert # not set on customer record.)"}
              </>
            ) : (
              <>
                <strong>TAX EXEMPT customer</strong> — no sales tax will be
                charged on this bill. Tax block on the printable invoice
                will show $0.00 with the cert citation.
                {customer?.taxExemptCert
                  ? ` Cert: ${customer.taxExemptCert}.`
                  : " (Cert # not set on customer record.)"}
              </>
            )}
            {certExpired && (
              <div style={{ marginTop: "6px", fontWeight: 600 }}>
                ⚠ Cert expired {customer.taxExemptCertExpires} — verify with
                customer and refresh before sending.
              </div>
            )}
          </div>
        );
      })()}

      {/* Taxes & fees */}
      <div style={{ marginTop: "12px" }}>
        <strong style={{ fontSize: "13px" }}>Taxes &amp; Fees</strong>
        {taxes.map((t) => (
          <div key={t.id} style={{ ...cell, marginTop: "4px" }}>
            <input
              style={{ ...smallInput, width: "180px" }}
              placeholder="Name (Sales tax, EPA fee...)"
              value={t.name}
              onChange={(e) => editTax(t.id, "name", e.target.value)}
            />
            <select
              style={{ ...smallInput, width: "110px" }}
              value={t.mode}
              onChange={(e) => editTax(t.id, "mode", e.target.value)}
            >
              <option value="percent">% of bill</option>
              <option value="flat">Flat fee</option>
            </select>
            {t.mode === "percent" ? (
              <input
                style={smallInput}
                type="number"
                placeholder="Rate %"
                value={t.rate}
                onChange={(e) => editTax(t.id, "rate", e.target.value)}
              />
            ) : (
              <input
                style={smallInput}
                type="number"
                placeholder="Amount $"
                value={t.amount}
                onChange={(e) => editTax(t.id, "amount", e.target.value)}
              />
            )}
            <span style={{ fontSize: "12px" }}>= {money(taxLineAmount(t))}</span>{" "}
            <button
              style={{ ...styles.button, color: "red", padding: "2px 6px" }}
              onClick={() => removeLine(setTaxes)(t.id)}
            >
              x
            </button>
          </div>
        ))}
        <button style={styles.button} onClick={addTax}>
          + Tax / fee line
        </button>
      </div>
      </fieldset>

      {isQuoted && (
        <div
          style={{
            marginTop: "14px",
            padding: "10px 12px",
            background: "#fff7e6",
            border: "1px solid #f0c674",
            borderRadius: "4px",
            fontSize: "13px",
          }}
        >
          <div style={{ fontWeight: "bold", color: "#8a5a00" }}>
            Locked at quoted price: {money(quotedAmount)}
            {job.quoteNumber ? ` (Quote #${job.quoteNumber})` : ""}
          </div>
          <div style={{ marginTop: "4px", color: "#5a4000" }}>
            Customer pays the quoted price regardless of lines below.
            Line items track P&amp;L only — variance vs the quote shows in
            the summary below.
          </div>
        </div>
      )}

      {/* Summary / P&L */}
      <div
        style={{
          marginTop: "14px",
          paddingTop: "10px",
          borderTop: "1px solid #ddd",
          fontSize: "14px",
        }}
      >
        <div>Total cost: {money(totalCost)}</div>
        <div>Invoice (billable) subtotal: {money(totalBillable)}</div>
        <div style={{ fontWeight: "bold", color: margin >= 0 ? "green" : "red" }}>
          Gross profit: {money(margin)}
        </div>
        <div style={{ fontSize: "13px", color: "#666" }}>
          Margin: {marginPct.toFixed(1)}% (of price) • Markup:{" "}
          {markupPct.toFixed(1)}% (of cost)
        </div>
        {taxAmount > 0 && <div>Taxes &amp; fees: {money(taxAmount)}</div>}
        {isQuoted && (
          <>
            <div style={{ marginTop: "4px", color: "#666" }}>
              T&amp;M total (if billed as time + materials): {money(grandTotal)}
            </div>
            <div
              style={{
                color: quoteVariance >= 0 ? "green" : "red",
                fontWeight: "bold",
              }}
            >
              Quote variance:{" "}
              {quoteVariance >= 0 ? "+" : ""}
              {money(quoteVariance)}{" "}
              <span style={{ fontWeight: "normal", color: "#666" }}>
                ({quoteVariance >= 0 ? "under quote — gain" : "over quote — loss"})
              </span>
            </div>
          </>
        )}
        {(job.bills || []).length > 0 ? (
          // Multi-bill running totals. Mirrors the QuickBooks Progress
          // Invoicing pattern: contract value / billed to date / remaining.
          // "Total project value" is the full grand total (today's
          // customerTotal); "Billed to date" sums bills[].grandTotal;
          // "Remaining" is the math. Both sides of the bill table see the
          // same shape so contractor + customer reconcile to the same picture.
          (() => {
            const billedToDate = (job.bills || []).reduce(
              (s, b) => s + (b.grandTotal || 0),
              0
            );
            const totalProject = customerTotal;
            const remaining = totalProject - billedToDate;
            return (
              <>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    marginTop: "4px",
                  }}
                >
                  Total project value: {money(totalProject)}
                </div>
                <div style={{ fontSize: "13px", color: "#1a5fb4" }}>
                  Billed to date ({job.bills.length} bill
                  {job.bills.length === 1 ? "" : "s"}): {money(billedToDate)}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: remaining > 0.001 ? "#7a5400" : "#1a5f1f",
                  }}
                >
                  Remaining to bill: {money(Math.max(0, remaining))}
                </div>
              </>
            );
          })()
        ) : (
          <div style={{ fontWeight: "bold", fontSize: "15px", marginTop: "4px" }}>
            Total Due (customer pays): {money(customerTotal)}
          </div>
        )}
      </div>

      {job.billFinalized && (
        <div
          style={{
            marginTop: "10px",
            fontSize: "12px",
            color: "#1a7f37",
            fontWeight: "bold",
          }}
        >
          Bill finalized{(job.billFinalizedByName || job.billFinalizedBy)
            ? ` by ${job.billFinalizedByName || job.billFinalizedBy}`
            : ""}
          {job.billFinalizedAt
            ? ` on ${new Date(job.billFinalizedAt).toLocaleDateString()}`
            : ""}
          .
        </div>
      )}

      <div style={{ marginTop: "10px" }}>
        {(job.bills || []).length > 0 ? (
          // Multi-bill (progress billing) flow. Bill-level reopen / void is
          // intentionally not exposed yet — Phase 2.C void/reissue covers
          // that. Until then, mid-stream bill correction needs admin to
          // delete the line stamps manually via the data console. Rare in
          // practice (Justin's stated discipline is "review before send").
          isFinalized ? (
            <span style={{ fontSize: "12px", color: "#666" }}>
              Job closed. To make changes, use the future Void Bill flow
              (Phase 2.C); contact support to undo a bill in the meantime.
            </span>
          ) : (
            <>
              <button style={styles.button} onClick={save} disabled={saving}>
                {saving ? "Saving..." : "Save Estimated Bill"}
              </button>
              {showLineItemButtons && (
                <>
                  <button
                    style={{
                      ...styles.button,
                      background: "#1a5fb4",
                      color: "white",
                    }}
                    onClick={() => startBill("partial", "line-item")}
                    disabled={saving}
                  >
                    + Progress Bill (line items)
                  </button>
                </>
              )}
              {showPercentageButtons && (
                <button
                  style={{
                    ...styles.button,
                    background: "#1a5fb4",
                    color: "white",
                  }}
                  onClick={() => startBill("partial", "percentage")}
                  disabled={saving}
                  title="Bill a percentage of the contracted price (Phase D)"
                >
                  + Progress Bill (%)
                </button>
              )}
              {showLineItemButtons && (
                <button
                  style={{
                    ...styles.button,
                    background: "#1a7f37",
                    color: "white",
                  }}
                  onClick={() => startBill("final", "line-item")}
                  disabled={saving}
                >
                  Final Bill (line items) &amp; Close Job
                </button>
              )}
              {showPercentageButtons && (
                <button
                  style={{
                    ...styles.button,
                    background: "#1a7f37",
                    color: "white",
                  }}
                  onClick={() => startBill("final", "percentage")}
                  disabled={saving}
                  title="Bill the remaining percent of the contracted price (Phase D)"
                >
                  Final Bill (%) &amp; Close Job
                </button>
              )}
            </>
          )
        ) : isFinalized ? (
          canEditJobsInner ? (
            <button
              style={styles.button}
              onClick={reopenBill}
              disabled={saving}
            >
              {saving ? "Reopening..." : "Reopen Bill"}
            </button>
          ) : (
            <span style={{ fontSize: "12px", color: "#666" }}>
              Bill finalized. Export PDF above to re-send to customer.
            </span>
          )
        ) : (
          <>
            <button style={styles.button} onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save Estimated Bill"}
            </button>
            <button
              style={{
                ...styles.button,
                background: "#1a5fb4",
                color: "white",
              }}
              onClick={() => startBill("partial", "line-item")}
              disabled={saving}
            >
              + Progress Bill (line items)
            </button>
            {canBillPercentage && (
              <button
                style={{
                  ...styles.button,
                  background: "#1a5fb4",
                  color: "white",
                }}
                onClick={() => startBill("partial", "percentage")}
                disabled={saving}
                title="Bill a percentage of the contracted price (Phase D)"
              >
                + Progress Bill (%)
              </button>
            )}
            <button
              style={{
                ...styles.button,
                background: "#1a7f37",
                color: "white",
              }}
              onClick={finalizeBill}
              disabled={saving}
            >
              Finalize Bill &amp; Close Job
            </button>
          </>
        )}
        {savedMsg && (
          <span style={{ fontSize: "12px", color: "green", marginLeft: "8px" }}>
            {savedMsg}
          </span>
        )}
      </div>

      {/* Whole-job bill export. Hidden until the job has actually been
          billed — there's nothing to export from a still-draft Estimated
          Bill. For multi-bill jobs, each bill in the "Bills issued" panel
          above has its own per-bill Export PDF; this section's full /
          itemized formats are mostly useful for legacy single-finalize
          jobs and as a contractor-side total summary on closed jobs. */}
      {(isFinalized || (job.bills || []).length > 0) && (
        <div style={{ marginTop: "10px" }}>
          <label style={{ fontSize: "13px" }}>Bill format: </label>
          <select
            value={billFormat}
            onChange={(e) => setBillFormat(e.target.value)}
          >
            <option value="summary">Summary (description + total)</option>
            <option value="itemized">Itemized (line items, no cost)</option>
            <option value="full">Full breakdown (cost + margin — gov't)</option>
          </select>{" "}
          <button style={styles.button} onClick={() => exportBill(billFormat)}>
            Export Bill
          </button>
        </div>
      )}

      <PdfActionModal
        pending={pdfPending}
        title="Export Bill"
        onPrint={handlePdfPrint}
        onSave={handlePdfSave}
        onCancel={() => setPdfPending(null)}
      />

      {progressBillType && (
        <ProgressBillModal
          initialType={progressBillType}
          billingMode={progressBillMode}
          job={{
            ...job,
            // Pass the IN-MEMORY costing (with any unsaved edits like new
            // lines or taxable toggles) so the modal's UNBILLED filter
            // reflects what's currently on screen.
            costing: { ...job.costing, labor, materials, expenses, fuel },
          }}
          customer={customer}
          taxes={taxes}
          onCancel={() => setProgressBillType(null)}
          onConfirm={onConfirmBill}
        />
      )}

      {/* Payment-entry modal (Phase B AR). */}
      <PaymentEntryModal
        open={!!paymentTarget}
        title="Record customer payment"
        subtitle={
          paymentTarget
            ? `${paymentTarget.invoiceNumber || "(no invoice #)"} — ${
                paymentTarget.description || ""
              }`
            : ""
        }
        billTotal={paymentTarget?.grandTotal || 0}
        alreadyPaid={paymentTarget?.paidAmount || 0}
        onCancel={() => setPaymentTarget(null)}
        onConfirm={async (payment) => {
          await actions.recordBillPayment(
            job.id,
            paymentTarget.id,
            payment
          );
          setPaymentTarget(null);
        }}
      />

      {/* Void invoice modal (Phase A). Reason picker is required; "other"
          also requires a free-text note. INV# is preserved; only the
          voided/voidReason fields get stamped on the bill. */}
      {voidTarget && (
        <div
          onClick={cancelVoid}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "10px",
              padding: "20px",
              width: "100%",
              maxWidth: "420px",
              boxShadow: "0 10px 32px rgba(0,0,0,0.3)",
            }}
          >
            <h3 style={{ margin: "0 0 8px", color: "#b91c1c" }}>
              Void invoice {voidTarget.invoiceNumber || ""}?
            </h3>
            <div style={{ fontSize: "12px", color: "#444", marginBottom: "12px" }}>
              Total {money(voidTarget.grandTotal || 0)} —{" "}
              {voidTarget.description || "(no description)"}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#555",
                marginBottom: "10px",
                padding: "8px",
                background: "#fff8e0",
                border: "1px solid #f0d27a",
                borderRadius: "4px",
              }}
            >
              The invoice number stays on the record (marked voided) for
              audit. Costing line items on this bill will be freed for
              re-billing on a fresh Progress Bill.
              {voidTarget.type === "final" && (
                <>
                  <br />
                  <strong>This was the FINAL bill</strong> — the job will
                  reopen so you can bill again.
                </>
              )}
            </div>
            <div style={{ marginBottom: "8px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600 }}>
                Reason:
              </label>
              <br />
              <select
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="">— Pick a reason —</option>
                {VOID_REASON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600 }}>
                Note{voidReason === "other" ? " (required)" : " (optional)"}:
              </label>
              <textarea
                value={voidNote}
                onChange={(e) => setVoidNote(e.target.value)}
                style={{
                  ...styles.textarea,
                  width: "100%",
                  marginTop: "2px",
                }}
                rows={3}
                placeholder={
                  voidReason === "other"
                    ? "Describe the reason"
                    : "Anything else to track"
                }
              />
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                style={{ ...styles.button, flex: 1 }}
                onClick={cancelVoid}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.button,
                  flex: 1,
                  background: "#b91c1c",
                  color: "white",
                  fontWeight: 600,
                }}
                onClick={submitVoid}
                disabled={
                  !voidReason ||
                  (voidReason === "other" && !voidNote.trim())
                }
              >
                Void invoice
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
